import { Suspense, useContext } from 'react';
import { Card } from 'react-bootstrap';

import { OpenAlexContext } from '../OpenAlexContext';
import { CITATION_FORMAT_REFERENCE } from '../reference/endpoint';
import { Reference } from '../reference/Reference';
import { type DOI } from '../utils/doi';
import { useLocalGetEndpoint } from '../utils/fetching';
import { Loading } from '../utils/Loading';
import { CitedByList } from './CitedByList';
import { DOICitationPublication } from './DOICItationPublication';
import {
  OPENALEX_CITATIONS_BY_ID,
  OPENALEX_FULLTEXT_SEARCH,
  OPENALEX_WORK_BY_DOI,
} from './endpoints';

export interface DOICitedProps {
  doi: DOI;
}

export function DOICited({ doi }: DOICitedProps) {
  const openAlexApiKey = useContext(OpenAlexContext);

  const reference = useLocalGetEndpoint({
    endpoint: CITATION_FORMAT_REFERENCE,
    params: {
      doi: doi.citationPublicationDOI || '',
      lang: 'en-US',
      style: 'apa',
    },
    skipFetch: !doi.citationPublicationDOI,
  });

  const work = useLocalGetEndpoint({
    endpoint: OPENALEX_WORK_BY_DOI,
    params: {
      doi: `https://doi.org/${doi.doi}`,
      ...(openAlexApiKey && { api_key: openAlexApiKey }),
    },
  });

  const openAlexId = work?.id.split('/').pop();

  const citations = useLocalGetEndpoint({
    endpoint: OPENALEX_CITATIONS_BY_ID,
    params: {
      filter: `cites:${openAlexId}`,
      ...(openAlexApiKey && { api_key: openAlexApiKey }),
    },
    skipFetch: !openAlexId, // only fetch if Open Alex ID exists
  });

  const fulltextCitations = useLocalGetEndpoint({
    endpoint: OPENALEX_FULLTEXT_SEARCH,
    params: {
      filter: `fulltext.search:"${doi.doi}"`,
      ...(openAlexApiKey && { api_key: openAlexApiKey }),
    },
  });

  const mergedCitations = [
    ...(citations?.results ?? []),
    ...(fulltextCitations?.results ?? []),
  ];

  const uniqueCitations = [
    ...new Map(
      mergedCitations.map((citation) => [citation.id, citation]),
    ).values(),
  ];
  const mergedCitationsResults = {
    results: uniqueCitations,
    meta: {},
  };

  const hasContent = doi.citationPublicationDOI || uniqueCitations.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <Card>
      <Card.Header className="p-2">Cited by</Card.Header>
      <Card.Body className="p-2 pb-0">
        {doi.citationPublicationDOI && (
          <>
            <h6 style={{ fontSize: 'inherit' }}>Author-provided citation:</h6>
            {reference ? (
              <Reference reference={reference} copy />
            ) : (
              <DOICitationPublication doi={doi} />
            )}
          </>
        )}

        {uniqueCitations.length > 0 && (
          <div className="d-flex justify-content-between align-items-baseline">
            <h6 style={{ fontSize: 'inherit' }}>
              Automatically discovered citations:
            </h6>
            <span className="text-muted small">
              Citation data retrieved from{' '}
              <a
                href="https://openalex.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                OpenAlex
              </a>
            </span>
          </div>
        )}
      </Card.Body>

      {uniqueCitations.length > 0 && (
        <Suspense fallback={<Loading />}>
          <CitedByList citations={mergedCitationsResults} />
        </Suspense>
      )}
    </Card>
  );
}
